import React, { useState, useEffect } from 'react';
import { supabase, checkUsernameAvailable } from './supabaseClient';
import NavigationBar from './NavigationBar';
import HamburgerMenu from './HamburgerMenu';

function LoginRegisterPage({ navigateTo, goBack, cameFromPuzzle, onLogin, solveData }) {
  const [mode, setMode] = useState('register'); // 'register' or 'login'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showFounderModal, setShowFounderModal] = useState(false);
  
  // Form states
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // Context awareness
  const guestSolveTime = '6:23'; // Mock data

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Real-time username validation
  useEffect(() => {
    if (username.length === 0) {
      setUsernameError('');
      return;
    }

    if (username.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      return;
    }

    if (username.length > 15) {
      setUsernameError('Username must be 15 characters or less');
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(username)) {
      setUsernameError('Letters and numbers only');
      return;
    }

    // Check username availability with Supabase
    setUsernameChecking(true);
    const timer = setTimeout(async () => {
      try {
        const isAvailable = await checkUsernameAvailable(username);
        setUsernameChecking(false);
        if (!isAvailable) {
          setUsernameError('That username is already taken');
        } else {
          setUsernameError('');
        }
      } catch (error) {
        console.error('Error checking username:', error);
        setUsernameChecking(false);
        setUsernameError('');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!username || !email || !password) {
      alert('Please fill in all fields');
      return;
    }

    if (usernameError) {
      alert('Please fix username errors');
      return;
    }

    if (password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
      // 1. Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            username: username
          }
        }
      });

      if (authError) {
        alert(`Registration failed: ${authError.message}`);
        return;
      }

      if (!authData.user) {
        alert('Registration failed. Please try again.');
        return;
      }

      // 2. Update profile with username (the trigger creates profile, but we need to update username)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ username: username })
        .eq('id', authData.user.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
      }

      // 3. Call onLogin to set user state
      if (onLogin) {
        await onLogin(authData.user);
      }

      // 4. Show Founder modal only if came from puzzle
      if (cameFromPuzzle) {
        setShowFounderModal(true);
      } else {
        // Redirect to puzzle selection
        navigateTo('difficulty');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('An error occurred during registration. Please try again.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      alert('Please enter your email and password');
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (error) {
        alert(`Login failed: ${error.message}`);
        return;
      }

      if (!data.user) {
        alert('Login failed. Please try again.');
        return;
      }

      // Call onLogin to set user state
      if (onLogin) {
        await onLogin(data.user);
      }

      // Navigate based on context
      if (cameFromPuzzle) {
        navigateTo('result', { fromPuzzle: true, solveData: solveData });
      } else {
        navigateTo('difficulty');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('An error occurred during login. Please try again.');
    }
  };

  const handleFounderView = () => {
    navigateTo('founder');
    setShowFounderModal(false);
  };

  const handleFounderLater = () => {
    setShowFounderModal(false);
    navigateTo('result', { fromPuzzle: true, solveData: solveData });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      padding: isMobile ? '20px 16px' : '40px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {!isMobile && <NavigationBar currentPage="login" isLoggedIn={false} navigateTo={navigateTo} />}
      {isMobile && <HamburgerMenu currentPage="login" isLoggedIn={false} navigateTo={navigateTo} />}
      
      {/* Founder Invitation Modal */}
      {showFounderModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={handleFounderLater}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '40px 32px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              textAlign: 'center'
            }}
          >
            {/* Emoji */}
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>
              🎉
            </div>

            {/* Heading */}
            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 12px 0',
              lineHeight: '1.2'
            }}>
              You're early.
            </h2>

            {/* Body Copy */}
            <p style={{
              fontSize: '15px',
              color: '#4B5563',
              margin: '0 0 32px 0',
              lineHeight: '1.6'
            }}>
              You've been invited to join the SudokuMatch Founder Programme.
              <br /><br />
              Founders help shape the future of competitive Sudoku and receive early access, recognition, and lifetime perks.
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '12px'
            }}>
              <button
                onClick={handleFounderView}
                style={{
                  flex: '1',
                  padding: '14px 24px',
                  background: '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#E07B00';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FF8C00';
                }}
              >
                View Founder Options
              </button>
              <button
                onClick={handleFounderLater}
                style={{
                  flex: '1',
                  padding: '14px 24px',
                  background: '#F3F4F6',
                  color: '#4B5563',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#E5E7EB';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#F3F4F6';
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '32px',
        maxWidth: '500px',
        width: '100%'
      }}>
        {/* Back Button */}
        <button
          onClick={goBack}
          style={{
            position: 'absolute',
            top: isMobile ? '20px' : '32px',
            left: isMobile ? '16px' : '32px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '20px',
            transition: 'all 0.2s ease',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          ←
        </button>

        {/* Logo */}
        <h1 style={{
          fontSize: isMobile ? '32px' : '36px',
          fontWeight: '800',
          color: 'white',
          margin: '0 0 8px 0',
          letterSpacing: '-0.5px'
        }}>
          SudokuMatch
        </h1>

        {/* Tagline */}
        <p style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.8)',
          margin: '0',
          fontWeight: '500'
        }}>
          Daily Sudoku. Played Fair.
        </p>
      </div>

      {/* Main Card */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        maxWidth: '440px',
        width: '100%',
        overflow: 'hidden'
      }}>
        {/* Guest Context Banner */}
        {cameFromPuzzle && (
          <div style={{
            background: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
            padding: '16px 20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              marginBottom: '4px'
            }}>
              Save your result?
            </div>
            <div style={{
              fontSize: '13px',
              opacity: 0.9,
              lineHeight: '1.4'
            }}>
              Create an account to see your exact position on today's leaderboard and share your result.
            </div>
          </div>
        )}

        {/* Mode Toggle (Segmented Control) */}
        <div style={{
          padding: '20px 20px 0 20px'
        }}>
          <div style={{
            display: 'flex',
            background: '#F3F4F6',
            borderRadius: '10px',
            padding: '4px',
            marginBottom: '24px'
          }}>
            <button
              onClick={() => setMode('register')}
              style={{
                flex: '1',
                padding: '10px',
                background: mode === 'register' ? 'white' : 'transparent',
                color: mode === 'register' ? '#1F2937' : '#6B7280',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: mode === 'register' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              Register
            </button>
            <button
              onClick={() => setMode('login')}
              style={{
                flex: '1',
                padding: '10px',
                background: mode === 'login' ? 'white' : 'transparent',
                color: mode === 'login' ? '#1F2937' : '#6B7280',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: mode === 'login' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              Log In
            </button>
          </div>
        </div>

        {/* Forms */}
        <div style={{
          padding: '0 20px 24px 20px'
        }}>
          {mode === 'register' ? (
            <form onSubmit={handleRegister}>
              {/* Username Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '8px'
                }}>
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="3-15 characters, letters and numbers"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: usernameError ? '2px solid #F87171' : '1px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    if (!usernameError) {
                      e.target.style.border = '2px solid #2563EB';
                    }
                  }}
                  onBlur={(e) => {
                    if (!usernameError) {
                      e.target.style.border = '1px solid #E5E7EB';
                    }
                  }}
                />
                {usernameChecking && (
                  <div style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    marginTop: '6px'
                  }}>
                    Checking availability...
                  </div>
                )}
                {usernameError && !usernameChecking && (
                  <div style={{
                    fontSize: '13px',
                    color: '#DC2626',
                    marginTop: '6px'
                  }}>
                    {usernameError}
                  </div>
                )}
              </div>

              {/* Email Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '8px'
                }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '2px solid #2563EB';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #E5E7EB';
                  }}
                />
              </div>

              {/* Password Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '8px'
                }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '2px solid #2563EB';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #E5E7EB';
                  }}
                />
              </div>

              {/* Helper Copy */}
              <p style={{
                fontSize: '13px',
                color: '#6B7280',
                margin: '0 0 24px 0',
                lineHeight: '1.5'
              }}>
                Create an account to save your results, appear on today's leaderboard, and receive shareable results.
              </p>

              {/* Submit Button */}
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#E07B00';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FF8C00';
                }}
              >
                Create Account
              </button>

              {/* Terms and Privacy Agreement */}
              <p style={{
                fontSize: '12px',
                color: '#6B7280',
                textAlign: 'center',
                margin: '16px 0 0 0',
                lineHeight: '1.5'
              }}>
                By signing up, you agree to our{' '}
                <button
                  type="button"
                  onClick={() => navigateTo('terms')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#FF8C00',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '0',
                    textDecoration: 'underline'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#E07B00';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#FF8C00';
                  }}
                >
                  Terms
                </button>
                {' '}and{' '}
                <button
                  type="button"
                  onClick={() => navigateTo('privacy')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#FF8C00',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '0',
                    textDecoration: 'underline'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#E07B00';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#FF8C00';
                  }}
                >
                  Privacy Policy
                </button>
              </p>

              {/* Secondary Text */}
              <p style={{
                fontSize: '13px',
                color: '#9CA3AF',
                textAlign: 'center',
                margin: '8px 0 0 0'
              }}>
                Takes less than a minute.
              </p>
            </form>
          ) : (
            <form onSubmit={handleLogin}>
              {/* Email Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '8px'
                }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '2px solid #2563EB';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #E5E7EB';
                  }}
                />
              </div>

              {/* Password Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '8px'
                }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '2px solid #2563EB';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #E5E7EB';
                  }}
                />
              </div>

              {/* Forgot Password */}
              <div style={{
                textAlign: 'right',
                marginBottom: '24px'
              }}>
                <button
                  type="button"
                  onClick={() => navigateTo('passwordreset')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563EB',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '0',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.textDecoration = 'none';
                  }}
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#E07B00';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FF8C00';
                }}
              >
                Log In
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer Spacing */}
      <div style={{ height: '40px' }} />
    </div>
  );
}

export default LoginRegisterPage;
